import { useMemo } from 'react';
import { DatePicker, Input, Pagination, Table, Tabs } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import { Topbar } from '../../layouts/Topbar';
import {
  listModelUsage,
  listTenantUsage,
  usageQueryKeys,
  type ModelUsageItem,
  type TenantUsageListItem,
  type UsageListParams,
} from '../../api/usage';
import { UsageBreakdownTable } from './UsageBreakdownTable';
import { renderCredits } from './format';
import './usage.css';
import { shanghaiTodayText, usagePageSize, usagePagination } from '../../../../../libs/shared/src/billing/display';

const { RangePicker } = DatePicker;

function shanghaiToday(): Dayjs {
  return dayjs(shanghaiTodayText());
}

function defaultRange(): [Dayjs, Dayjs] {
  const to = shanghaiToday();
  return [to.subtract(13, 'day'), to];
}

function disableShortRange(current: Dayjs, info: { from?: Dayjs }) {
  return Boolean(info.from && Math.abs(current.diff(info.from, 'day')) < 6);
}

function parseDateParam(value: string | null, fallback: Dayjs): Dayjs {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return fallback;
  const d = dayjs(value, 'YYYY-MM-DD', true);
  return d.isValid() ? d : fallback;
}

export function UsagePage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'models' ? 'models' : 'tenants';
  const defaults = defaultRange();
  const from = parseDateParam(params.get('from'), defaults[0]).format('YYYY-MM-DD');
  const to = parseDateParam(params.get('to'), defaults[1]).format('YYYY-MM-DD');
  const page = Math.max(1, Number(params.get('page') || 1) || 1);
  const pageSize = usagePageSize(params.get('pageSize'));
  const search = params.get('search') ?? '';
  const model = params.get('model') ?? undefined;
  const providerKey = params.get('providerKey') ?? undefined;

  const filter: UsageListParams = useMemo(() => ({
    from,
    to,
    page,
    pageSize,
    sort: 'creditsUsed',
    search: search || undefined,
    model,
    providerKey,
  }), [from, to, page, pageSize, search, model, providerKey]);

  function patch(next: Record<string, string | undefined>) {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (!v) merged.delete(k);
      else merged.set(k, v);
    }
    setParams(merged, { replace: true });
  }

  const tenantsQuery = useQuery({
    queryKey: usageQueryKeys.tenants(filter),
    queryFn: () => listTenantUsage(filter),
    enabled: tab === 'tenants',
  });
  const modelsQuery = useQuery({
    queryKey: usageQueryKeys.models(filter),
    queryFn: () => listModelUsage(filter),
    enabled: tab === 'models',
  });

  const columns: ColumnsType<TenantUsageListItem> = [
    {
      title: '租户',
      key: 'tenant',
      width: 180,
      render: (_, row) => (
        <Link to={`/usage/tenants/${row.tenantId}?${new URLSearchParams({
          from, to, ...(model ? { model } : {}), ...(providerKey ? { providerKey } : {}),
          back: params.toString(),
        }).toString()}`}>{row.displayName}</Link>
      ),
    },
    { title: 'code', dataIndex: 'tenantCode', width: 120 },
    { title: '当前余额', key: 'balance', width: 140, render: (_, row) => renderCredits(row.balance, row.isUnlimited) },
    { title: '累计消耗积分', dataIndex: 'totalCreditsUsed', width: 140, render: (v: string) => renderCredits(v) },
    { title: '期间消耗积分', dataIndex: 'periodCreditsUsed', width: 140, render: (v: string) => renderCredits(v) },
    { title: '未计费', dataIndex: 'unpricedCount', width: 90 },
  ];

  const activeQuery = tab === 'models' ? modelsQuery : tenantsQuery;
  const failed = activeQuery.isError;

  return (
    <>
      <Topbar crumbs={[{ label: '运营管理' }, { label: '用量管理' }]} />
      <div className="page usage-page">
        <div className="page-header">
          <div className="page-header__title">
            <h1>用量管理</h1>
          </div>
          <div className="page-header__actions">
            <button type="button" className="btn btn-secondary" onClick={() => activeQuery.refetch()} disabled={activeQuery.isFetching}>
              <ReloadOutlined spin={activeQuery.isFetching} /> <span>刷新</span>
            </button>
          </div>
        </div>

        <div className="usage-toolbar">
          <RangePicker
            value={[dayjs(from), dayjs(to)]}
            allowClear={false}
            disabledDate={disableShortRange}
            onChange={(range) => {
              if (!range?.[0] || !range[1]) return;
              patch({ from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD'), page: '1' });
            }}
          />
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={tab === 'models' ? '搜索模型 / 供应商' : '搜索租户 code / 名称'}
            value={search}
            style={{ width: 280 }}
            onChange={(e) => patch({ search: e.target.value || undefined, page: '1' })}
            onPressEnter={() => patch({ page: '1' })}
          />
        </div>

        <Tabs
          activeKey={tab}
          onChange={(key) => patch({ tab: key, page: '1' })}
          items={[
            { key: 'tenants', label: '按租户' },
            { key: 'models', label: '按模型' },
          ]}
        />

        {failed && (
          <div className="usage-error">
            <span>用量查询失败</span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => activeQuery.refetch()}>重试</button>
          </div>
        )}

        {!failed && tab === 'tenants' && (
          <>
            <Table<TenantUsageListItem>
              className="usage-table"
              rowKey="tenantId"
              columns={columns}
              dataSource={tenantsQuery.data?.items ?? []}
              loading={tenantsQuery.isLoading}
              pagination={false}
              scroll={{ x: 900 }}
              size="small"
            />
            <Pagination
              current={page}
              pageSize={pageSize}
              {...usagePagination}
              total={tenantsQuery.data?.total ?? 0}
              style={{ marginTop: 16, textAlign: 'right' }}
              onChange={(next, size) => patch({ page: String(size === pageSize ? next : 1), pageSize: String(size) })}
            />
          </>
        )}

        {!failed && tab === 'models' && (
          <>
            <UsageBreakdownTable<ModelUsageItem>
              rows={modelsQuery.data?.items ?? []}
              loading={modelsQuery.isLoading}
              showTenantCount
              onInspectTenants={(row) => patch({
                tab: 'tenants',
                model: row.model,
                providerKey: row.providerKey,
                page: '1',
              })}
            />
            <Pagination
              current={page}
              pageSize={pageSize}
              {...usagePagination}
              total={modelsQuery.data?.total ?? 0}
              style={{ marginTop: 16, textAlign: 'right' }}
              onChange={(next, size) => patch({ page: String(size === pageSize ? next : 1), pageSize: String(size) })}
            />
          </>
        )}
      </div>
    </>
  );
}
